// Library
import { initializeApp } from 'firebase/app';
import { getFirestore } from "firebase/firestore";
import { firebaseConfig } from './FirebaseConfig';
import { runTransaction, doc } from "firebase/firestore";

// Functions
import QueryDocument from '../Library/QueryDocument';

/**
 * Creates a new request and returns request ID(s)
 * @param {number} idCount
 * 
 * ```
 * const idCount = 2;
 * 
 *
 * CreatePoANumber(idCount).then((requestids) =>{
 * 
 *   const digitalRequestId = requestids[0];
 *   const initialApprovalRequestId = requestids[1];
 * 
 * });
 * 
 * ```
 * 
 * Documentation
 * - N/A
 */

export default async function CreatePoANumber(idCount, retryCount, requestIds) {

  try {

    //Firestore Database reference
    const app = initializeApp(firebaseConfig);
    const db = getFirestore(app);

    //------------------------------------------------------
    //  Validate number of IDs requested
    //------------------------------------------------------

    if (idCount === undefined) {

      idCount = 1;

    }

    //------------------------------------------------------
    //  Validate number of retry attempts
    //------------------------------------------------------

    if (retryCount === undefined) {

      retryCount = 0;

    }

    if (retryCount > 3) throw new Error(`Function CreatePoANumber failed exceed maxiumum attempts of 3.`);

    //------------------------------------------------------
    //  Validate requestIds
    //------------------------------------------------------

    if (requestIds === undefined) {

      requestIds = [];

    }

    //------------------------------------------------------
    //  Check if all request IDs have been created
    //------------------------------------------------------

    if (idCount === requestIds.length) {

      return requestIds;

    }

    //------------------------------------------------------
    //  Create a Firebase transaction
    //    1. Query for the previous document
    //    2. Create the next document
    //    IF there is a conflict, retry up to three attempts!
    //------------------------------------------------------

    const requests = await QueryDocument('requests', [
      ['tasktype', 'in', ['Commercial Review', 'Digital Commercial Request']]
    ], ['createddate', 'desc'], 1);

    return runTransaction(db, async (transaction) => {

      const previousRequestId = requests[0]?.requestid;

      // Create Financial Year prefix
      const today = new Date();
      let FYYear = new Date(new Date(today).setMonth(today.getMonth() + 6)); // Add 6 months to current date
      FYYear = `${FYYear.getUTCFullYear()}`
      FYYear = `FY${FYYear.slice(2)}-`;
      
      let requestid = FYYear;
      
      //------------------------------------------------------
      //  Define NEW task number
      //------------------------------------------------------
      
      // NO existing tickets found
      if (requests.length === 0 || previousRequestId === undefined) {
      
        requestid = `${requestid}00001`; // TO DO - Need to get the starting number from commercial team

      // Next FY check, reset on the new FY
      } else if (previousRequestId !== undefined && previousRequestId.includes(requestid) === false) {
      
        requestid = `${requestid}00001`; // TO DO - Need to get the starting number from commercial team
      
      // Catch All, increment the existing ticket number
      } else {
      
        //------------------------------------------------------
        //  Increment from existing task
        //------------------------------------------------------
      
        const previousNumber = parseInt(previousRequestId.slice(5)); // Extract number from requestid
        const newNumber = previousNumber + 1;
      
        // Add required zero's
        const loopLimit = 5 - `${newNumber}`.length;
      
        let count = 0;
        while (count < loopLimit) {
      
          count += 1;
      
          requestid = `${requestid}0`;
      
        }
      
        // Finally, add 1 to the task number
        requestid = `${requestid}${newNumber}`;
      
      }

      //------------------------------------------------------
      //  Using a 'transaction' check if document exists!
      //------------------------------------------------------

      const docRef = doc(db, 'requests', requestid);
      const docCheck = await transaction.get(docRef);

      if (docCheck.exists()) throw new Error('Found existing document!');

      //------------------------------------------------------
      //  Generate the request ID
      //------------------------------------------------------

      const document = {
        'requestid': requestid,
        'createddate': new Date(),
        'tasktype': 'Commercial Review',
        'poanumber': requestid,
        'createdby': {
          'email': '',
          'givenname': '',
          'surname': ''
        },
        'lastmodifiedby': {
          'email': '',
          'givenname': '',
          'surname': ''
        }
      };

      //Set collection
      const collectionDocRef = doc(db, 'requests', requestid);

      //------------------------------------------------------
      //  Using a 'transaction' write the new document
      //------------------------------------------------------

      transaction.set(collectionDocRef, document, { merge: false });

      return requestid;

    })

    //------------------------------------------------------
    //  Settle 'runTransaction'
    //------------------------------------------------------

    .then((requestid) => {

      requestIds.push(requestid);

      return CreatePoANumber(idCount, retryCount, requestIds);

    })
    
    //------------------------------------------------------
    //  'runTransaction' failed, re-run with incremented 'retryCount' 
    //------------------------------------------------------

    .catch(() => {

      retryCount += 1;

      // Yes this is a loop, but it has a backoff :-)
      return CreatePoANumber(idCount, retryCount, requestIds);

    });

    //------------------------------------------------------
    //  Handle unexpected errors
    //------------------------------------------------------

  } catch (error) {

    throw new Error(`Function CreatePoANumber failed to complete, error ${error}`);

  }

}
